import { useState } from "react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@libs/zapi-ui/ds/components/dialog";
import { FieldError } from "@libs/zapi-ui/ds/components/field";
import { Input } from "@libs/zapi-ui/ds/components/input";
import { Label } from "@libs/zapi-ui/ds/components/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@libs/zapi-ui/ds/components/select";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { sendInvite } from "../services/sendInvite";
import { z } from "zod";
import { toast } from "sonner";
import { UserRoleSchema, AccessModeSchema } from "@libs/shared-permissions";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { getMemberRoles, getAccessModes } from "../const/const";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { AxiosError } from "axios";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { TAccessMode } from "../services/schemas";
interface InviteUserModalProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  organizationId: number;
  onSuccess: () => void;
}

export const getFormSchema = (t: TFunction<"settings">) =>
  z.object({
    role: UserRoleSchema.refine((val) => val, { message: t("invite.roleRequired") }),
    accessMode: AccessModeSchema,
    firstName: z
      .string()
      .min(1, { message: t("invite.firstNameRequired") })
      .min(2, { message: t("invite.firstNameTooShort") }),
    lastName: z
      .string()
      .min(1, { message: t("invite.lastNameRequired") })
      .min(2, { message: t("invite.lastNameTooShort") }),
    email: z.string({ message: t("invite.emailRequired") }).email({ message: t("invite.emailInvalid") })
  });

export function InviteUserModal({ isOpen, onOpenChange, organizationId, onSuccess }: InviteUserModalProps) {
  const { t } = useTranslation(["settings", "common"]);
  const { role: currentUserRole, accessMode: currentUserAccessMode } = useMeStore();
  const currentUserHasEditAccess = hasEditAccess(currentUserRole, currentUserAccessMode);

  const [formData, setFormData] = useState<{
    role: string;
    accessMode: string;
    firstName: string;
    lastName: string;
    email: string;
  }>({
    role: "",
    accessMode: currentUserHasEditAccess ? "edit" : "read",
    firstName: "",
    lastName: "",
    email: ""
  });

  function handleChange(field: keyof typeof formData, value: string) {
    const updatedData = { ...formData, [field]: value };
    if (field === "role" && value === "systemAdmin") {
      updatedData.accessMode = "edit";
    }
    setFormData(updatedData);
    setErrors({ ...errors, [field]: undefined });
  }

  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{
    role?: string[];
    firstName?: string[];
    lastName?: string[];
    email?: string[];
  }>({});

  const handleClose = () => {
    setIsLoading(false);
    onOpenChange(false);
    setFormData({
      role: "",
      accessMode: currentUserHasEditAccess ? "edit" : "read",
      firstName: "",
      lastName: "",
      email: ""
    });
    setErrors({});
  };

  const handleSubmit = async () => {
    const validatedFields = getFormSchema(t).safeParse(formData);
    if (!validatedFields.success) {
      setErrors(validatedFields.error.flatten().fieldErrors);
      toast.error(t("invite.invalidForm"));
      return;
    }

    const data = {
      orgId: organizationId.toString(),
      email: formData.email,
      role: formData.role,
      accessMode: formData.accessMode as TAccessMode,
      firstName: formData.firstName,
      lastName: formData.lastName
    };

    setIsLoading(true);
    const [, error] = await tryCatch<null, AxiosError<{ message: string }>>(sendInvite(data));
    handleClose();
    setIsLoading(false);
    if (error) {
      toast.error(error.response?.data.message || t("invite.sendFailed"));
      console.error("Error sending invite:", error);
      return;
    }

    onSuccess();
    toast.success(t("invite.sent"));
  };

  const roles = getMemberRoles(t, currentUserRole);
  const accessModes = getAccessModes(t);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
    >
      <DialogContent closeLabel={t("common:close")} className="sm:max-w-[525px]">
        <DialogHeader>
          <DialogTitle className="font-medium">{t("invite.title")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-6 py-4">
          <div className="grid gap-2">
            <Label htmlFor="member-role">{t("invite.memberRole")}</Label>
            <Select
              items={roles}
              value={formData.role || null}
              onValueChange={(value) => value !== null && handleChange("role", value)}
            >
              <SelectTrigger id="member-role" data-testid="invite-user-role-trigger">
                <SelectValue placeholder={t("invite.selectOption")} />
              </SelectTrigger>
              <SelectContent>
                {roles.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`invite-user-role-${r.value}-item`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="-mt-2 text-[14px] text-red-500">{errors.role?.[0]}</p>
          </div>

          {formData.role !== "systemAdmin" && (
            <div className="grid gap-2">
              <Label htmlFor="access-mode">{t("invite.accessMode")}</Label>
              <Select
                items={accessModes}
                value={formData.accessMode || null}
                onValueChange={(value) => value !== null && handleChange("accessMode", value)}
                disabled={!currentUserHasEditAccess}
              >
                <SelectTrigger id="access-mode" data-testid="invite-user-access-mode-trigger">
                  <SelectValue placeholder={t("invite.selectAccessMode")} />
                </SelectTrigger>
                <SelectContent>
                  {accessModes.map((m) => (
                    <SelectItem key={m.value} value={m.value} data-testid={`invite-user-access-mode-${m.value}-item`}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex gap-4">
            <div className="flex basis-1/2 flex-col gap-2">
              <Label htmlFor="first-name">{t("invite.firstName")}</Label>
              <div className="relative flex flex-1 flex-col gap-2">
                <Input
                  id="first-name"
                  data-testid="invite-user-first-name-input"
                  placeholder={t("invite.firstNamePlaceholder")}
                  value={formData.firstName}
                  onChange={(e) => handleChange("firstName", e.target.value)}
                  aria-invalid={!!errors.firstName?.[0] || undefined}
                />
                {errors.firstName?.[0] && <FieldError>{errors.firstName[0]}</FieldError>}
              </div>
            </div>
            <div className="flex basis-1/2 flex-col gap-2">
              <Label htmlFor="last-name">{t("invite.lastName")}</Label>
              <div className="relative flex flex-1 flex-col gap-2">
                <Input
                  id="last-name"
                  data-testid="invite-user-last-name-input"
                  placeholder={t("invite.lastNamePlaceholder")}
                  value={formData.lastName}
                  onChange={(e) => handleChange("lastName", e.target.value)}
                  aria-invalid={!!errors.lastName?.[0] || undefined}
                />
                {errors.lastName?.[0] && <FieldError>{errors.lastName[0]}</FieldError>}
              </div>
            </div>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="email">{t("invite.email")}</Label>
            <div className="relative flex flex-1 flex-col gap-2">
              <Input
                id="email"
                data-testid="invite-user-email-input"
                type="email"
                placeholder={t("invite.emailPlaceholder")}
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                aria-invalid={!!errors.email?.[0] || undefined}
              />
              {errors.email?.[0] && <FieldError>{errors.email[0]}</FieldError>}
            </div>
          </div>
        </div>
        <DialogFooter className="justify-start!">
          <Button
            data-testid="invite-user-submit-button"
            type="submit"
            onClick={handleSubmit}
            className="w-full sm:w-auto"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                Loading...
              </>
            ) : (
              t("invite.send")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
